/**
 * Recent Projects Component
 * Shows a list of recently accessed projects for quick access
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { 
  Clock, 
  FolderOpen, 
  Music, 
  Trash2, 
  X,
  ChevronRight,
  Calendar
} from 'lucide-react';

const MAX_RECENT_PROJECTS = 8;

// Hook to manage recent projects
export const useRecentProjects = () => {
  const [recentProjects, setRecentProjects] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('string-sauce-recent-projects');
      if (saved) {
        setRecentProjects(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load recent projects:', e);
    }
  }, []);

  // Save to localStorage when changed
  const saveProjects = useCallback((projects) => {
    try {
      localStorage.setItem('string-sauce-recent-projects', JSON.stringify(projects));
    } catch (e) {
      console.error('Failed to save recent projects:', e);
    }
  }, []);

  // Add a project to recent list
  const addRecentProject = useCallback((project) => {
    setRecentProjects(prev => {
      // Remove if already exists (to move to top)
      const filtered = prev.filter(p => p.id !== project.id);
      // Add to beginning
      const updated = [
        {
          id: project.id,
          name: project.name || 'Untitled Project',
          lastOpened: new Date().toISOString(),
          trackCount: project.tracks?.length || 0,
          thumbnail: project.thumbnail || null,
        },
        ...filtered,
      ].slice(0, MAX_RECENT_PROJECTS);
      
      saveProjects(updated);
      return updated;
    });
  }, [saveProjects]);

  // Remove a project from recent list
  const removeRecentProject = useCallback((projectId) => {
    setRecentProjects(prev => {
      const updated = prev.filter(p => p.id !== projectId);
      saveProjects(updated);
      return updated;
    });
  }, [saveProjects]);

  // Clear all recent projects
  const clearRecentProjects = useCallback(() => {
    setRecentProjects([]);
    localStorage.removeItem('string-sauce-recent-projects');
  }, []);

  return {
    recentProjects,
    addRecentProject,
    removeRecentProject,
    clearRecentProjects,
  };
};

// Format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Recent Projects Panel
export const RecentProjectsPanel = ({ 
  isOpen, 
  onClose, 
  onSelectProject,
  currentProjectId 
}) => {
  const { recentProjects, removeRecentProject, clearRecentProjects } = useRecentProjects();

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="fixed left-4 top-20 bottom-4 w-[320px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
            data-testid="recent-projects-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                <h2 className="font-semibold text-white">Recent Projects</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-zinc-400 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Projects List */}
            <div className="flex-1 overflow-y-auto p-2">
              {recentProjects.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <FolderOpen className="w-12 h-12 text-zinc-600 mb-3" />
                  <p className="text-sm text-zinc-400 mb-1">No recent projects</p>
                  <p className="text-xs text-zinc-500">
                    Projects you open will appear here for quick access
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentProjects.map((project) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`group relative rounded-lg transition-colors ${
                        project.id === currentProjectId
                          ? 'bg-orange-500/10 border border-orange-500/30'
                          : 'hover:bg-zinc-800'
                      }`}
                    >
                      <button
                        onClick={() => onSelectProject(project.id)}
                        className="w-full text-left px-3 py-3 flex items-center gap-3"
                        data-testid={`recent-project-${project.id}`}
                      >
                        {/* Thumbnail or icon */}
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <Music className="w-5 h-5 text-orange-400" />
                        </div>
                        
                        {/* Project info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {project.name}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatRelativeTime(project.lastOpened)}
                            </span>
                            <span>•</span>
                            <span>{project.trackCount} tracks</span>
                          </div>
                        </div>
                        
                        {/* Arrow indicator */}
                        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                      </button>
                      
                      {/* Delete button (shown on hover) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRecentProject(project.id);
                        }}
                        className="absolute right-10 top-1/2 -translate-y-1/2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                        title="Remove from recent"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {recentProjects.length > 0 && (
              <div className="px-4 py-3 border-t border-zinc-700 bg-zinc-800/50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearRecentProjects}
                  className="w-full text-zinc-500 hover:text-red-400 text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Clear Recent Projects
                </Button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Compact recent projects button with dropdown
export const RecentProjectsButton = ({ onSelectProject, currentProjectId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { recentProjects } = useRecentProjects();

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="text-zinc-400 hover:text-white relative"
        data-testid="recent-projects-btn"
        title="Recent Projects"
      >
        <Clock className="w-5 h-5" />
        {recentProjects.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
            {recentProjects.length}
          </span>
        )}
      </Button>
      
      <RecentProjectsPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSelectProject={(id) => {
          onSelectProject(id);
          setIsOpen(false);
        }}
        currentProjectId={currentProjectId}
      />
    </>
  );
};

export default RecentProjectsPanel;
